// src/pages/AdminInventory/AdminInventory.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { Auth } from "aws-amplify";
import AdminNav from "../../components/AdminNav";
import "./AdminInventory.css";

const API_BASE = process.env.REACT_APP_API_BASE;

async function authHeader() {
  const session = await Auth.currentSession();
  const token = session.getAccessToken().getJwtToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function lowStockBadge(available) {
  const n = Number(available || 0);
  if (n <= 0) return <Badge bg="danger">Out</Badge>;
  if (n <= 3) return <Badge bg="warning">Low</Badge>;
  return <Badge bg="success">OK</Badge>;
}

export default function AdminInventory() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [editModal, setEditModal] = useState({
    show: false,
    sku: "",
    available: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const idPayload = user?.signInUserSession?.idToken?.payload || {};
        const groups = idPayload["cognito:groups"] || idPayload["custom:groups"] || [];

        if (Array.isArray(groups) && (groups.includes("Support") || groups.includes("Admin"))) {
          setAuthorized(true);
          await fetchInventory();
        } else {
          setAuthorized(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth error:", err);
        setAuthorized(false);
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      if (!API_BASE) {
        throw new Error("Missing REACT_APP_API_BASE. Add it to your env and restart the dev server.");
      }

      const headers = await authHeader();
      const res = await fetch(`${API_BASE}/admin/inventory`, { method: "GET", headers });

      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || `Failed to load inventory: ${res.status}`);
      }

      const data = await res.json();
      const list = (data.items || [])
        .map((x) => ({
          sku: x.sku,
          available: Number(x.available || 0),
        }))
        .sort((a, b) => (a.sku > b.sku ? 1 : -1));

      setItems(list);
    } catch (err) {
      console.error("Fetch inventory error:", err);
      setError(err?.message || "Failed to load inventory.");
    } finally {
      setLoading(false);
    }
  };

  const patchInventory = async (sku, body) => {
    if (!API_BASE) throw new Error("Missing REACT_APP_API_BASE.");
    const headers = await authHeader();

    const res = await fetch(`${API_BASE}/admin/inventory/${encodeURIComponent(sku)}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new Error(b.message || b.error || `Failed to update inventory: ${res.status}`);
    }

    return res.json().catch(() => ({}));
  };

  const openEdit = (row) => {
    setError("");
    setSuccessMsg("");
    setEditModal({
      show: true,
      sku: row.sku,
      available: String(row.available ?? 0),
    });
  };

  const closeEdit = () => {
    setEditModal({ show: false, sku: "", available: "" });
  };

  const saveExact = async () => {
    const sku = editModal.sku;
    const n = Number(editModal.available);

    if (!sku) return;
    if (!Number.isFinite(n) || n < 0) {
      setError("Available must be a number >= 0.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMsg("");

    try {
      await patchInventory(sku, { available: n });

      setItems((prev) => prev.map((it) => (it.sku === sku ? { ...it, available: n } : it)));
      setSuccessMsg(`Updated ${sku} available to ${n}.`);
      closeEdit();
    } catch (err) {
      console.error("Save exact error:", err);
      setError(err?.message || "Failed to save inventory.");
    } finally {
      setSaving(false);
    }
  };

  const adjustDelta = async (sku, delta) => {
    setError("");
    setSuccessMsg("");

    // compute next from the freshest state
    const current = items.find((x) => x.sku === sku)?.available ?? 0;
    const next = Math.max(0, Number(current) + delta);

    // Optimistic UI
    setItems((prev) =>
      prev.map((it) => (it.sku === sku ? { ...it, available: next } : it))
    );

    try {
      await patchInventory(sku, { available: next });
      setSuccessMsg(`Adjusted ${sku} by ${delta > 0 ? "+" : ""}${delta}.`);
    } catch (err) {
      console.error("Adjust delta error:", err);
      setError(err?.message || "Failed to adjust inventory.");
      fetchInventory();
    }
  };

  if (loading && !items.length) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4 text-center">
            <Spinner animation="border" /> Loading inventory…
          </div>
        </Container>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="admin-page-wrapper">
        <Container className="admin-page-shell">
          <div className="py-4">
            <Alert variant="danger">You’re not authorized to view this page.</Alert>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Container className="admin-page-shell">
        <AdminNav />

        <div className="admin-page-header">
          <div className="admin-page-header-left">
            <div className="admin-page-header-icon">📦</div>
            <div>
              <h3 className="admin-page-title">Inventory</h3>
              <div className="admin-page-subtitle">
                Manage SmartTag stock levels. Updates are written to DynamoDB.
              </div>
            </div>
          </div>

          <div className="admin-page-header-right">
            <Button variant="outline-secondary" size="sm" onClick={fetchInventory}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="admin-page-body">
          {error && <Alert variant="danger">{error}</Alert>}
          {successMsg && <Alert variant="success">{successMsg}</Alert>}

          <div className="admin-panel">
            {items.length === 0 ? (
              <p className="mb-0">No inventory rows found.</p>
            ) : (
              <Table className="admin-table" striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Available</th>
                    <th>Status</th>
                    <th className="admin-col-adjust">Quick Adjust</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((row) => (
                    <tr key={row.sku}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <strong>{row.sku}</strong>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{row.available}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{lowStockBadge(row.available)}</td>

                      <td>
                        <Row className="g-2 admin-adjust-row">
                          <Col xs="auto">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => adjustDelta(row.sku, -1)}
                            >
                              −1
                            </Button>
                          </Col>
                          <Col xs="auto">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => adjustDelta(row.sku, +1)}
                            >
                              +1
                            </Button>
                          </Col>
                          <Col xs="auto">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => adjustDelta(row.sku, -5)}
                            >
                              −5
                            </Button>
                          </Col>
                          <Col xs="auto">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => adjustDelta(row.sku, +5)}
                            >
                              +5
                            </Button>
                          </Col>
                        </Row>
                      </td>

                      <td style={{ whiteSpace: "nowrap" }}>
                        <Button size="sm" variant="outline-primary" onClick={() => openEdit(row)}>
                          Set Exact
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Set Exact Modal */}
          <Modal className="admin-modal" show={editModal.show} onHide={closeEdit} centered>
            <Modal.Header closeButton>
              <Modal.Title>Set Inventory – {editModal.sku}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group controlId="inventoryAvailable">
                <Form.Label>Available</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={editModal.available}
                  onChange={(e) =>
                    setEditModal((prev) => ({ ...prev, available: e.target.value }))
                  }
                />
                <Form.Text className="text-muted">Set the exact stock count (0+).</Form.Text>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeEdit}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveExact} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </Container>
    </div>
  );
}
